import { bearerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { oauthSetupSteps } from "../oauthSetupSteps";
import { oauthAuth } from "../standardAuthPatterns";
import logo from "./logo.svg";

const credential = productVariant({
  authLabel: "Existing access token",
  identifier: "tiktok",
  protocol: "http",
  defaultUrlPattern: () => "https://open\\.tiktokapis\\.com/.*",
  auth: (app) => bearerAuth(app.secret.accessToken),
  steps: [
    {
      title: "Set up TikTok API access",
      items: [
        {
          kind: "markdown",
          content:
            "Create an app at the [TikTok for Developers portal](https://developers.tiktok.com/) and complete the OAuth flow to obtain an access token.",
        },
        {
          kind: "input",
          label: "Access token",
          required: true,
          target: { kind: "secrets", field: "accessToken" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: () => "https://open.tiktokapis.com/v2/user/info/",
  },
});

export const tiktok = defineProduct({
  name: "TikTok",
  tagLine: "Short-form video platform",
  logo,
  tags: ["communication"],
  variants: [
    credential,
    productVariant({
      identifier: "tiktokOAuth",
      authLabel: "OAuth",
      protocol: "http",
      defaultUrlPattern: () => "https://open\\.tiktokapis\\.com/v2/.*",
      auth: oauthAuth,
      steps: oauthSetupSteps({
        identifier: "tiktokOAuth",
        registrationUrl: "https://developers.tiktok.com/",
        scopes: ["user.info.basic"],
        instructions:
          "Enable Login Kit. Enter the application’s Client Key as the Client ID.",
      }),
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () =>
          "https://www.tiktok.com/v2/auth/authorize/",
        tokenEndpoint: () => "https://open.tiktokapis.com/v2/oauth/token/",
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "db",
        scopes: () => ["user.info.basic"],
        pkce: () => false,
        normalizeAuthorizationRequest: ({
          client_id,
          scope,
          ...parameters
        }) => ({
          ...parameters,
          client_key: client_id!,
          ...(scope ? { scope: scope.split(" ").join(",") } : {}),
        }),
        normalizeTokenRequest: ({ client_id, ...parameters }) => ({
          ...parameters,
          client_key: client_id!,
        }),
        normalizeTokenResponse: (body) => ({
          ...body,
          ...(typeof body.scope === "string"
            ? { scope: body.scope.split(",").join(" ") }
            : {}),
        }),
      },
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://open.tiktokapis.com/v2/user/info/?fields=open_id,display_name",
          expectedBodyContains: ['"open_id"', '"user"'],
        }),
      },
    }),
  ],
});
